import { InfoCircleOutlined } from "@ant-design/icons";
import type { CloudAuditSessionView as CloudAuditView } from "@p0security/shared/integrations/sessions/types";
import { Button, Flex, Input, Select, Spin, Switch, Typography } from "antd";
import { Export, ExportOption } from "components/Export";
import { GraphTooltip } from "components/GraphTooltip";
import { ChangeEvent, useCallback } from "react";

import { CloudAuditRowsTable } from "./CloudAuditRowsTable";
import { SessionViewHeader } from "./SessionViewHeader";

export const CloudAuditSessionView = ({
  view,
  loading,
  quantity,
  updateQuantity,
  search,
  updateSearch,
  includeResourceSearch,
  updateIncludeResourceSearch,
  getSession,
  exportOptions,
  requestId,
}: {
  view: CloudAuditView;
  loading: boolean;
  quantity: number;
  updateQuantity: (value: number) => void;
  search: string;
  updateSearch: (value: ChangeEvent<HTMLInputElement>) => void;
  includeResourceSearch: boolean;
  updateIncludeResourceSearch: (value: boolean) => void;
  getSession: () => void;
  exportOptions: Record<string, ExportOption<unknown>>;
  requestId: string;
}) => {
  const handleSearch = useCallback(() => {
    getSession();
  }, [getSession]);

  const isAwsCloudAudit = view.source === "aws-cloudtrail";

  return (
    <>
      <SessionViewHeader view={view} fallbackTitle="Session History" />
      <Flex align="center" justify="flex-end" style={{ marginBottom: "16px" }}>
        <Export
          data={view.rows}
          filename={`session-history-${String(view.rows?.[0]?.principal ?? "")}-${requestId}`}
          title="Export session history"
          options={exportOptions}
        />
      </Flex>
      <CloudAuditRowsTable
        rows={view.rows}
        source={view.source}
        update={
          <Flex gap="small" align="center">
            <Input
              placeholder="Search relevant logs (e.g. s3, aws-cli, <resource-name>)"
              value={search}
              onChange={updateSearch}
              onPressEnter={handleSearch}
              allowClear
              style={{ width: 400 }}
              size="small"
            />
            {search && isAwsCloudAudit && (
              <Flex gap="small" align="center">
                <Typography.Text>Include resources</Typography.Text>
                <GraphTooltip title="This will incorporate an additional search for resources.">
                  <InfoCircleOutlined />
                </GraphTooltip>
                <Switch
                  checked={includeResourceSearch}
                  onChange={updateIncludeResourceSearch}
                />
              </Flex>
            )}
            <Typography.Text type="secondary">Showing latest</Typography.Text>
            <Select
              value={quantity}
              onChange={updateQuantity}
              style={{ width: "64px" }}
              size="small"
            >
              <Select.Option value={10}>10</Select.Option>
              <Select.Option value={20}>20</Select.Option>
              <Select.Option value={50}>50</Select.Option>
              <Select.Option value={100}>100</Select.Option>
            </Select>
            <Button
              disabled={loading}
              type="primary"
              onClick={getSession}
              size="small"
              style={{ width: "fit-content" }}
            >
              {loading && <Spin size="small" />}
              {loading ? "Fetching..." : search ? "Search" : "Update"}
            </Button>
          </Flex>
        }
      />
    </>
  );
};
